/**
 * Momentum Algorithm (formerly "Adaptive")
 *
 * Tracks driver momentum from historical race patterns. Weights recent form
 * against season-long Monte Carlo simulation.
 *
 * Blends the last 2 actual race scores (recentScore) with a Monte Carlo
 * simulation trained on all historical races with exponential decay weighting.
 *
 * At pre_weekend: 50% recentScore + 50% MC output.
 * As live session data arrives (FP, quali), the MC becomes more accurate
 * and its weight increases accordingly.
 *
 * recentScore = 0.6 × R-1 + 0.4 × R-2 (Budget Boost formula).
 * MC encodes track suitability and season context via component-model
 * parameter estimation (qualifying position, race delta, overtake probability).
 */

import { MOMENTUM_CONFIG } from "../../prediction";
import {
  estimateConstructorParams,
  estimateDriverParams,
} from "../../prediction/component-model";
import { runDriverMonteCarlo } from "../../prediction/monte-carlo";
import {
  applySeasonDiscount,
  computeWeights,
  weightedMean,
} from "../../prediction/recency-weights";
import { adjustParamsForStage } from "../../prediction/stage-adjustments";
import { getTrackClassification } from "../../statistics/track-classification";
import { calculateSimplePriceChange } from "../price-change";
import type {
  PredictionAlgorithm,
  PredictionResult,
  TeamCalculatorConstructor,
  TeamCalculatorDriver,
  TeamCalculatorRaceContext,
} from "../types";

// ============================================================================
// Recent Form Weights
// ============================================================================

/**
 * How much weight to give the last 2 actual race scores (recentScore) vs MC output.
 *
 * At pre_weekend, no live data: 50% recent form, 50% MC.
 * As live sessions arrive, the MC becomes increasingly informed (FP, sprint quali,
 * qualifying positions) so we shift aggressively toward MC.
 * At lock stages (after_fp3 / after_sprint_quali) and beyond, recent form is 10%:
 * enough to credit historical recovery ability (e.g. VER from the back) but not
 * enough to override strong live session signals (null qualifying time = P20 start).
 */
const RECENT_FORM_WEIGHT: Record<string, number> = {
  pre_weekend: 0.5, // No session data — recent form anchors prediction
  after_fp1: 0.3, // Some live data — shift toward session-informed MC
  after_fp2: 0.2, // More live data — session signal dominates
  after_fp3: 0.1, // LOCK stage (regular) — MC heavily session-informed
  after_sprint_quali: 0.1, // LOCK stage (sprint) — equivalent to after_fp3
  after_sprint: 0.1, // Post-sprint — session signal dominant
  after_quali: 0.1, // Grid known — keep small: signals recovery ability
  after_race: 0.0, // Race complete — no prediction
};

function getRecentFormWeight(stage: string): number {
  return RECENT_FORM_WEIGHT[stage] ?? 0.5;
}

// ============================================================================
// Race Context Builder
// ============================================================================

function buildRaceContext(raceContext: TeamCalculatorRaceContext) {
  return {
    raceSlug: raceContext.raceName ?? "",
    trackType: getTrackClassification(raceContext.raceName ?? ""),
    isSprintWeekend: raceContext.isSprintWeekend,
    raceRound: raceContext.raceRound,
    season: raceContext.season,
  };
}

export const momentumAlgorithm: PredictionAlgorithm = {
  metadata: {
    id: "momentum",
    name: "Momentum",
    description:
      "Tracks driver momentum from historical race patterns. Weights recent form against season-long Monte Carlo simulation.",
    icon: "Brain",
    isAdvanced: true,
  },

  predictDriver(
    driver: TeamCalculatorDriver,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const ctx = buildRaceContext(raceContext);
    const baseParams = estimateDriverParams(driver, ctx, MOMENTUM_CONFIG);
    const params = adjustParamsForStage(
      baseParams,
      driver,
      raceContext.currentStage,
    );
    const result = runDriverMonteCarlo(
      params,
      raceContext.isSprintWeekend,
      10_000,
      raceContext.season,
    );

    let finalPoints = result.expectedPoints;
    const history = driver.raceHistory;

    if (history.length >= 1) {
      const r1 = history[history.length - 1]; // most recent race
      const r2 = history.length >= 2 ? history[history.length - 2] : null;

      let recentScore: number;
      if (r2 && r2.season >= raceContext.season) {
        recentScore = 0.6 * r1.totalPoints + 0.4 * r2.totalPoints;
      } else {
        recentScore = r1.totalPoints;
      }

      // Fix A: When R-1 was a DNF, recentScore = totalPoints (e.g., -14 for Piastri).
      // This double-counts the DNF since the MC already models dnfProb.
      // Use MC expected points as proxy instead.
      if (r1.raceDnf) {
        recentScore = result.expectedPoints;
      }

      // Discount pit-lane-start inflation
      const r1IsPitLaneStart = r1.qualifyingPosition == null;
      if (r1IsPitLaneStart && !r1.raceDnf) {
        const r1Contribution =
          r2 && r2.season >= raceContext.season ? 0.6 : 1.0;
        const discount = 0.35;
        recentScore -= r1Contribution * r1.totalPoints * (1 - discount);
      }

      // Sprint bonus adjustment
      if (
        raceContext.isSprintWeekend &&
        !r1.isSprintWeekend &&
        !r1IsPitLaneStart &&
        !r1.raceDnf &&
        result.components
      ) {
        const sprintBonus =
          result.components.sprintPositionEV +
          result.components.sprintOvertakesEV +
          result.components.sprintFastestLapEV;
        recentScore += sprintBonus;
      }

      const recentWeight = getRecentFormWeight(raceContext.currentStage);
      finalPoints =
        recentWeight * recentScore + (1 - recentWeight) * result.expectedPoints;
    }

    return {
      entityId: driver.driverSlug,
      predictedPoints: Math.round(finalPoints * 10) / 10,
      confidence: result.confidence,
      p10: result.p10,
      p90: result.p90,
      stdDev: result.stdDev,
      dnfProb: result.dnfProb,
      components: result.components,
    };
  },

  predictConstructor(
    ctor: TeamCalculatorConstructor,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const history = ctor.raceHistory;

    if (history.length === 0) {
      return {
        entityId: ctor.slug,
        predictedPoints: 20,
        confidence: 0.1,
      };
    }

    const reversed = [...history].reverse();
    const rawWeights = computeWeights(reversed.length, MOMENTUM_CONFIG);
    const weights = applySeasonDiscount(
      rawWeights,
      reversed,
      raceContext.season,
    );
    const points = reversed.map((h) => h.totalPoints);
    let seasonAvg = weightedMean(points, weights);

    const currentSeasonRaces = reversed.filter(
      (h) => h.season >= raceContext.season,
    );
    const currentSeasonSprintRaces = reversed.filter(
      (h) => h.isSprintWeekend && h.season >= raceContext.season,
    );
    let prevAvg = 0;
    if (currentSeasonRaces.length < 3) {
      const prevSeasonPoints = reversed
        .filter((h) => h.season < raceContext.season)
        .map((h) => h.totalPoints);
      if (prevSeasonPoints.length > 0) {
        prevAvg =
          prevSeasonPoints.reduce((a, b) => a + b, 0) / prevSeasonPoints.length;
        const getConfidence = (prior: number, races: number) => {
          const tier =
            prior >= 40 ? "strong" : prior >= 25 ? "mid" : "volatile";
          if (races === 1)
            return tier === "strong" ? 0.5 : tier === "mid" ? 0.35 : 0.15;
          return tier === "strong" ? 0.65 : tier === "mid" ? 0.5 : 0.35;
        };
        const currentConfidence = getConfidence(
          prevAvg,
          currentSeasonRaces.length,
        );
        seasonAvg =
          currentConfidence * seasonAvg + (1 - currentConfidence) * prevAvg;
      }
    }

    if (raceContext.isSprintWeekend) {
      if (currentSeasonSprintRaces.length >= 2) {
        const sprintPts = currentSeasonSprintRaces.map((h) => h.totalPoints);
        const sprintAvg =
          sprintPts.reduce((a, b) => a + b, 0) / sprintPts.length;
        seasonAvg = sprintAvg * 0.7 + seasonAvg * 0.3;
      } else {
        const isLowConfidenceTeam = prevAvg > 0 && prevAvg < 25;
        const SPRINT_UPLIFT =
          currentSeasonSprintRaces.length === 0 && isLowConfidenceTeam
            ? 0.1
            : 0.2;
        seasonAvg *= 1 + SPRINT_UPLIFT;
      }
    }

    const ctorR1 = history[history.length - 1];
    const ctorR2 = history.length >= 2 ? history[history.length - 2] : null;
    let recentScore: number;
    if (ctorR2 && ctorR2.season >= raceContext.season) {
      recentScore = 0.6 * ctorR1.totalPoints + 0.4 * ctorR2.totalPoints;
    } else {
      recentScore = ctorR1.totalPoints;
    }

    const isVolatileWithSingleNonSprint =
      prevAvg > 0 &&
      prevAvg < 25 &&
      currentSeasonRaces.length === 1 &&
      !ctorR1.isSprintWeekend;
    if (
      raceContext.isSprintWeekend &&
      !ctorR1.isSprintWeekend &&
      !isVolatileWithSingleNonSprint
    ) {
      const SPRINT_UPLIFT = 0.2;
      recentScore *= 1 + SPRINT_UPLIFT;
    }

    const baseRecentWeight = getRecentFormWeight(raceContext.currentStage);
    const recentWeightFactor = (() => {
      if (currentSeasonRaces.length !== 1) return 1.0;
      if (prevAvg >= 40) return 0.6;
      if (prevAvg >= 25) return 0.4;
      return 0.2;
    })();
    const recentWeight = baseRecentWeight * recentWeightFactor;
    let avgPoints = recentWeight * recentScore + (1 - recentWeight) * seasonAvg;

    const stageSessionPos = (() => {
      const stage = raceContext.currentStage;
      if (stage === "after_quali") return ctor.qualiAvgPosition ?? 20;
      if (stage === "after_fp3")
        return ctor.fp3AvgPosition ?? ctor.fp2AvgPosition ?? null;
      if (stage === "after_sprint_quali")
        return ctor.sprintQualiAvgPosition ?? 20;
      if (stage === "after_sprint")
        return ctor.sprintQualiAvgPosition ?? ctor.fp1AvgPosition ?? null;
      if (stage === "after_fp2")
        return ctor.fp2AvgPosition ?? ctor.fp1AvgPosition ?? null;
      if (stage === "after_fp1") return ctor.fp1AvgPosition ?? null;
      return null;
    })();

    if (stageSessionPos != null) {
      const sessionFactor = Math.max(0.5, 1.25 - (stageSessionPos - 1) * 0.025);
      const stageWeight: Record<string, number> = {
        after_fp1: 0.2,
        after_fp2: 0.35,
        after_fp3: 0.5,
        after_sprint_quali: 0.5,
        after_sprint: 0.4,
        after_quali: 0.65,
      };
      const w = stageWeight[raceContext.currentStage] ?? 0;
      avgPoints *= 1 - w + w * sessionFactor;
    }

    const ctorParams = estimateConstructorParams(ctor, MOMENTUM_CONFIG);

    return {
      entityId: ctor.slug,
      predictedPoints: Math.round(avgPoints * 10) / 10,
      confidence: ctorParams.confidence,
    };
  },

  calculatePriceChange(
    predictedPoints: number,
    currentPrice: number,
    tier: "A" | "B",
    priceFloor: number,
  ): number {
    return calculateSimplePriceChange(
      predictedPoints,
      tier,
      currentPrice,
      priceFloor,
    );
  },
};
